import { useState, useMemo, type FC } from 'react';
import { History, Filter } from 'lucide-react';
import { BookingCard } from '../../../components/Dashboard';
import { useMyBookings } from '../../../hooks/useBookings';

type StatusFilter = 'all' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All History' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'NO_SHOW', label: 'No Show' },
];

const SkeletonCard: FC = () => (
  <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4 animate-pulse">
    <div className="flex justify-between mb-3">
      <div>
        <div className="h-5 w-40 bg-gray-200 dark:bg-slate-700 rounded mb-2" />
        <div className="h-4 w-24 bg-gray-200 dark:bg-slate-700 rounded" />
      </div>
      <div className="h-6 w-20 bg-gray-200 dark:bg-slate-700 rounded-full" />
    </div>
    <div className="grid grid-cols-2 gap-2 mb-4">
      <div className="h-4 w-28 bg-gray-200 dark:bg-slate-700 rounded" />
      <div className="h-4 w-24 bg-gray-200 dark:bg-slate-700 rounded" />
    </div>
  </div>
);

export const BookingHistorySection: FC = () => {
  const { data: bookings = [], isLoading, error, refetch } = useMyBookings();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const historyBookings = useMemo(() => {
    const now = new Date();
    const past = bookings.filter((b) => new Date(b.startTime) <= now || b.status === 'CANCELLED');
    return statusFilter === 'all' ? past : past.filter((b) => b.status === statusFilter);
  }, [bookings, statusFilter]);

  const filterLabel = STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label;

  if (error) {
    return (
      <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
        <History className="w-16 h-16 text-red-400 dark:text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2">Failed to Load History</h3>
        <p className="text-gray-600 dark:text-slate-400 mb-4">{error.message}</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-6">Booking History</h1>

      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-500 dark:text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            aria-label="Filter bookings by status"
            className="border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <span className="text-sm text-gray-500 dark:text-slate-400">
          {historyBookings.length} {historyBookings.length === 1 ? 'booking' : 'bookings'}
        </span>
      </div>

      {isLoading ? (
        <div className="space-y-4">{[1, 2, 3].map((i) => <SkeletonCard key={i} />)}</div>
      ) : historyBookings.length > 0 ? (
        <div className="space-y-4">
          {historyBookings.map((booking) => (
            <BookingCard key={booking.id} booking={booking} showActions={false} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
          <History className="w-16 h-16 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2">
            {statusFilter === 'all' ? 'No Booking History' : `No ${filterLabel} Bookings`}
          </h3>
          <p className="text-gray-600 dark:text-slate-400">
            {statusFilter === 'all' ? 'Your past bookings will appear here.' : 'Try selecting a different filter.'}
          </p>
        </div>
      )}
    </div>
  );
};
